import { forwardRef, useState, type InputHTMLAttributes } from 'react';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

/**
 * Input do Bow Redesign — variante "BowField":
 *  - rounded-2xl (1rem)
 *  - label uppercase tracking-wide acima
 *  - bg-white com borda navy sutil; foco realça em navy
 *  - type="password" ganha o olhinho de mostrar/ocultar (pedido de cliente,
 *    2026-10-17: errou a confirmação na tela de nova senha e não tinha como ver)
 */
export const Input = forwardRef<HTMLInputElement, Props>(
  ({ label, error, className = '', type, ...rest }, ref) => {
    const [revealed, setRevealed] = useState(false);
    const isPassword = type === 'password';

    return (
      <label className="flex flex-col gap-1.5">
        {label && (
          <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/55">
            {label}
          </span>
        )}
        <div className="relative">
          <input
            ref={ref}
            type={isPassword && revealed ? 'text' : type}
            // Senha à mostra vira type="text": sem isso o teclado do celular
            // passa a capitalizar e autocorrigir o que o cliente digita.
            {...(isPassword && {
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false
            })}
            className={`h-12 w-full rounded-2xl border border-bow-royal/10 bg-white px-4 text-[15px] font-medium text-bow-royal outline-none transition placeholder:text-bow-royal/40 focus:border-bow-royal/40 focus:shadow-soft [@media(max-height:740px)]:h-11 ${
              isPassword ? 'pr-12' : ''
            } ${className}`}
            {...rest}
          />
          {isPassword && (
            <button
              type="button"
              aria-label={revealed ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={revealed}
              // Não tira o foco do campo: o teclado fica aberto e o layout não pula.
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => setRevealed((v) => !v)}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-2xl text-bow-royal/45 transition hover:text-bow-royal"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                {revealed ? (
                  <>
                    <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a13.2 13.2 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 8 10 8a9.7 9.7 0 0 0 5.39-1.61" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <path d="M2 2l20 20" />
                  </>
                ) : (
                  <>
                    <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>
        {error && <span className="px-1 text-sm text-bow-red">{error}</span>}
      </label>
    );
  }
);
Input.displayName = 'Input';
